import React, { useState, useEffect, FormEvent } from 'react';
import {
    Subscription,
    getAllSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription,
} from '../../Services/SubscriptionService';

interface SubscriptionFormData {
    name: string;
    monthlyPriceEur: string;
    annualPriceEur: string;
    extraProjectPriceEur: string;
    projectsPerYearLimit: string;
}

const emptyForm: SubscriptionFormData = {
    name: '',
    monthlyPriceEur: '',
    annualPriceEur: '',
    extraProjectPriceEur: '',
    projectsPerYearLimit: '',
};

const eurToCents = (value: string): number => {
    const n = Number(value.replace(',', '.'));
    if (Number.isNaN(n) || n < 0) return 0;
    return Math.round(n * 100);
};

const centsToEur = (cents: number): string => (cents / 100).toFixed(2);

const SubscriptionManagement: React.FC = () => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<Subscription | null>(null);
    const [formData, setFormData] = useState<SubscriptionFormData>(emptyForm);
    const [formError, setFormError] = useState<string | null>(null);
    const [formLoading, setFormLoading] = useState(false);

    useEffect(() => {
        loadSubscriptions();
    }, []);

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(null), 4000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    const loadSubscriptions = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAllSubscriptions();
            setSubscriptions(data);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Error cargando suscripciones');
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditing(null);
        setFormData(emptyForm);
        setFormError(null);
        setShowModal(true);
    };

    const openEditModal = (s: Subscription) => {
        setEditing(s);
        setFormData({
            name: s.name,
            monthlyPriceEur: centsToEur(s.monthlyPriceCents),
            annualPriceEur: centsToEur(s.annualPriceCents),
            extraProjectPriceEur: centsToEur(s.extraProjectPriceCents ?? 0),
            projectsPerYearLimit: String(s.projectsPerYearLimit),
        });
        setFormError(null);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditing(null);
        setFormData(emptyForm);
        setFormError(null);
    };

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            setFormError('El nombre es obligatorio.');
            return false;
        }
        const limit = Number(formData.projectsPerYearLimit);
        if (!Number.isInteger(limit) || limit < 0) {
            setFormError('El número de proyectos debe ser un entero mayor o igual a 0.');
            return false;
        }
        if (formData.monthlyPriceEur === '' || Number.isNaN(Number(formData.monthlyPriceEur.replace(',', '.')))) {
            setFormError('El precio mensual no es válido.');
            return false;
        }
        if (formData.annualPriceEur === '' || Number.isNaN(Number(formData.annualPriceEur.replace(',', '.')))) {
            setFormError('El precio anual no es válido.');
            return false;
        }
        if (formData.extraProjectPriceEur === '' || Number.isNaN(Number(formData.extraProjectPriceEur.replace(',', '.')))) {
            setFormError('El precio por proyecto extra no es válido.');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setFormError(null);
        if (!validateForm()) return;
        try {
            setFormLoading(true);
            const payload = {
                name: formData.name.trim(),
                monthlyPriceCents: eurToCents(formData.monthlyPriceEur),
                annualPriceCents: eurToCents(formData.annualPriceEur),
                extraProjectPriceCents: eurToCents(formData.extraProjectPriceEur),
                projectsPerYearLimit: Number(formData.projectsPerYearLimit),
            };
            if (editing) {
                await updateSubscription(editing.subscriptionId, payload);
                setSuccessMessage('Suscripción actualizada correctamente.');
            } else {
                await createSubscription(payload);
                setSuccessMessage('Suscripción creada correctamente.');
            }
            closeModal();
            await loadSubscriptions();
        } catch (err: any) {
            setFormError(err.response?.data?.error || err.message || 'Error al guardar la suscripción.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (s: Subscription) => {
        if (!window.confirm(`¿Eliminar la suscripción "${s.name}"?`)) return;
        try {
            setActionLoading(s.subscriptionId);
            await deleteSubscription(s.subscriptionId);
            setSubscriptions(subscriptions.filter(x => x.subscriptionId !== s.subscriptionId));
            setSuccessMessage('Suscripción eliminada correctamente.');
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Error eliminando suscripción');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Suscripciones</h2>
                <button className="btn btn-primary" onClick={openCreateModal} style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}>
                    + Nueva Suscripción
                </button>
            </div>

            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
            )}
            {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {successMessage}
                    <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
                </div>
            )}

            {subscriptions.length === 0 ? (
                <div className="card">
                    <div className="card-body text-center py-5">
                        <h5 className="text-muted">No hay suscripciones definidas</h5>
                        <p className="text-muted mb-4">Crea una suscripción para empezar a asignarla a arquitectos.</p>
                        <button className="btn btn-primary" onClick={openCreateModal}>+ Nueva Suscripción</button>
                    </div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Precio mensual</th>
                                <th>Precio anual</th>
                                <th>Proyecto extra</th>
                                <th>Proyectos/año</th>
                                <th className="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subscriptions.map(s => (
                                <tr key={s.subscriptionId}>
                                    <td>{s.name}</td>
                                    <td>{centsToEur(s.monthlyPriceCents)} €</td>
                                    <td>{centsToEur(s.annualPriceCents)} €</td>
                                    <td>{centsToEur(s.extraProjectPriceCents ?? 0)} €</td>
                                    <td>{s.projectsPerYearLimit}</td>
                                    <td className="text-end">
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                className="btn btn-link text-dark text-decoration-none"
                                                onClick={() => openEditModal(s)}
                                                disabled={actionLoading === s.subscriptionId}
                                            >
                                                Editar
                                            </button>
                                            <button
                                                className="btn btn-link text-danger text-decoration-none"
                                                onClick={() => handleDelete(s)}
                                                disabled={actionLoading === s.subscriptionId}
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{editing ? 'Editar Suscripción' : 'Nueva Suscripción'}</h5>
                                    <button type="button" className="btn-close" onClick={closeModal} disabled={formLoading}></button>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body">
                                        {formError && <div className="alert alert-danger">{formError}</div>}
                                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                                            Duración: 12 meses (fijo)
                                        </p>
                                        <div className="mb-3">
                                            <label htmlFor="name" className="form-label">Nombre *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                disabled={formLoading}
                                                placeholder="Ej. Pro"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="row">
                                            <div className="mb-3 col-md-6">
                                                <label htmlFor="monthlyPriceEur" className="form-label">Precio mensual (€) *</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="form-control"
                                                    id="monthlyPriceEur"
                                                    value={formData.monthlyPriceEur}
                                                    onChange={(e) => setFormData({ ...formData, monthlyPriceEur: e.target.value })}
                                                    disabled={formLoading}
                                                />
                                            </div>
                                            <div className="mb-3 col-md-6">
                                                <label htmlFor="annualPriceEur" className="form-label">Precio anual (€) *</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="form-control"
                                                    id="annualPriceEur"
                                                    value={formData.annualPriceEur}
                                                    onChange={(e) => setFormData({ ...formData, annualPriceEur: e.target.value })}
                                                    disabled={formLoading}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="extraProjectPriceEur" className="form-label">
                                                Precio por proyecto extra (€) *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="form-control"
                                                id="extraProjectPriceEur"
                                                value={formData.extraProjectPriceEur}
                                                onChange={(e) => setFormData({ ...formData, extraProjectPriceEur: e.target.value })}
                                                disabled={formLoading}
                                            />
                                            <small className="text-muted">
                                                Coste adicional cuando un arquitecto quiere comprar un proyecto suelto tras agotar su cuota anual.
                                            </small>
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="projectsPerYearLimit" className="form-label">Proyectos por año *</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                className="form-control"
                                                id="projectsPerYearLimit"
                                                value={formData.projectsPerYearLimit}
                                                onChange={(e) => setFormData({ ...formData, projectsPerYearLimit: e.target.value })}
                                                disabled={formLoading}
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer d-flex justify-content-end gap-2" style={{ alignItems: 'stretch' }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline-dark"
                                            onClick={closeModal}
                                            disabled={formLoading}
                                            style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={formLoading}
                                            style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}
                                        >
                                            {formLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Guardando...
                                                </>
                                            ) : editing ? 'Guardar cambios' : 'Crear Suscripción'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SubscriptionManagement;
